/**
 * useConfirmCartMove: "Get it from <branch>" asks before the cart moves.
 * ---------------------------------------------------------------------------
 * Shopper journey audit (4 Oct 2026). "Get it from Ntinda" on one product
 * opens that branch, and the cart is bought from one branch at a time, so
 * every item in it moves too. A shopper tapping it for one product does not
 * expect their whole basket to move without a word, so when the cart holds
 * items from another branch this asks the same question as the location
 * picker and the apps: "Move your cart to <branch>?", "Move cart" or
 * "Stay at <branch>". An empty cart, or one already there, just goes.
 *
 * The page renders components/OutletSwitchDialog from `pending`, `confirm`
 * and `cancel`, and passes `request` as the badge's `onGetItFrom`.
 * ---------------------------------------------------------------------------
 */
import { useCallback, useState } from "react";

/**
 * @param {object}   params
 * @param {Function} params.needsAsk  (location) => boolean, the page's cartBoundElsewhere
 * @param {Function} params.onMove    (location) => void, the page's chooseLocation
 * @returns {{ request: Function, pending: object|null, confirm: Function, cancel: Function }}
 */
const useConfirmCartMove = ({ needsAsk, onMove }) => {
  const [pending, setPending] = useState(null);

  const request = useCallback(
    (location) => {
      if (!location) return;
      if (typeof needsAsk === "function" && needsAsk(location)) {
        setPending(location);
        return;
      }
      if (typeof onMove === "function") onMove(location);
    },
    [needsAsk, onMove]
  );

  const cancel = useCallback(() => setPending(null), []);

  const confirm = useCallback(() => {
    const location = pending;
    setPending(null);
    if (location && typeof onMove === "function") onMove(location);
  }, [pending, onMove]);

  return { request, pending, confirm, cancel };
};

export default useConfirmCartMove;
